// VLAN-zone helpers (VLAN redesign 2026-05-31). Pure client-side mirror of
// the server's parseVlanRange bounds so the Networks create-form can show an
// inline error before the request. Server remains the source of truth.

export const SUBVENDO_VLAN_MIN = 10; // 2–9 reserved for PPPoE/WAN/infra
export const SUBVENDO_VLAN_MAX = 4094;

// Max zones a single create may add — mirrors the server's MAX_BULK_PER_REQUEST.
// Each new zone is pushed + verified on the router async; small batches keep the
// status feedback tight. Raised 3 -> 10 (Ray 2026-10-21): one batch is one router
// update, so ten at once is fewer updates than four small batches.
export const MAX_ZONES_PER_BATCH = 10;

const SPEC_RE = /^(\d{1,4})(?:\s*-\s*(\d{1,4}))?$/;

export function isValidSubvendoVlanId(id: number): boolean {
   return (
      Number.isInteger(id) && id >= SUBVENDO_VLAN_MIN && id <= SUBVENDO_VLAN_MAX
   );
}

/**
 * Parse a bulk-create spec from the Networks form: "11" → {start:11,end:11};
 * "11-100" → {start:11,end:100}. Returns null if malformed, out of the
 * sub-vendo range (10–4094), or inverted (end < start). Whitespace tolerant.
 */
export function parseZoneSpec(
   spec: string
): { start: number; end: number } | null {
   const m = String(spec).trim().match(SPEC_RE);
   if (!m) return null;
   const start = Number(m[1]);
   const end = m[2] !== undefined ? Number(m[2]) : start;
   if (!isValidSubvendoVlanId(start) || !isValidSubvendoVlanId(end)) return null;
   if (end < start) return null;
   return { start, end };
}

/** Convenience: is the spec usable as-is. */
export function isValidZoneSpec(spec: string): boolean {
   return parseZoneSpec(spec) !== null;
}

/** How many zones a spec would create, or null if the spec is invalid. */
export function zoneSpecCount(spec: string): number | null {
   const p = parseZoneSpec(spec);
   return p ? p.end - p.start + 1 : null;
}

/** Is the spec within the per-batch cap (and otherwise valid)? */
export function isWithinBatchLimit(spec: string): boolean {
   const n = zoneSpecCount(spec);
   return n !== null && n <= MAX_ZONES_PER_BATCH;
}
